import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Users, Search, Eye, UserPlus, X, ToggleLeft, ToggleRight, Edit2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HREmployees = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');
  const [showOnboardForm, setShowOnboardForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    designation: '',
    salary: '',
    date_of_joining: '',
    role: 'employee',
    pan: '',
    uan: '',
    bank_account: '',
    address: '',
    emergency_contact: ''
  });

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/employees`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching employees:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, getAuthHeader]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        // Update existing employee
        await axios.put(
          `${BACKEND_URL}/api/hr/employees/${editingEmployee.employee_id}`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('✓ Employee updated successfully!');
      } else {
        // Create new employee
        await axios.post(
          `${BACKEND_URL}/api/hr/employees`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('✓ Employee onboarded successfully!');
      }
      
      resetForm();
      fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleEdit = (employee) => {
    setFormData({
      full_name: employee.full_name || '',
      email: employee.email || '',
      password: '', // Don't populate password
      phone: employee.phone || '',
      department: employee.department || '',
      designation: employee.designation || '',
      salary: employee.salary || '',
      date_of_joining: employee.date_of_joining || '',
      role: 'employee',
      pan: employee.pan || '',
      uan: employee.uan || '',
      bank_account: employee.bank_account || '',
      address: employee.address || '',
      emergency_contact: employee.emergency_contact || ''
    });
    setEditingEmployee(employee);
    setShowOnboardForm(true);
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      phone: '',
      department: '',
      designation: '',
      salary: '',
      date_of_joining: '',
      role: 'employee',
      pan: '',
      uan: '',
      bank_account: '',
      address: '',
      emergency_contact: ''
    });
    setEditingEmployee(null);
    setShowOnboardForm(false);
  };

  const handleToggleStatus = async (employeeId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!confirm(`Are you sure you want to ${action} this employee?`)) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/hr/employees/${employeeId}/toggle-status`,
        {},
        { headers: getAuthHeader() }
      );
      alert(`Employee ${action}d successfully!`);
      fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update employee status');
    }
  };

  const departments = ['all', ...new Set(employees.map(emp => emp.department))];

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = selectedDept === 'all' || emp.department === selectedDept;
    return matchesSearch && matchesDept;
  });

  if (loading) return <div>Loading employees...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users size={28} />
            Employee Management
          </h2>
          <p className="text-gray-600 mt-1">Total: {filteredEmployees.length} employees</p>
        </div>
        <Button onClick={() => {
          if (showOnboardForm) {
            resetForm();
          } else {
            setShowOnboardForm(true);
          }
        }}>
          {showOnboardForm ? <X size={18} className="mr-2" /> : <UserPlus size={18} className="mr-2" />}
          {showOnboardForm ? 'Cancel' : 'Onboard Employee'}
        </Button>
      </div>

      {/* Onboarding/Edit Form */}
      {showOnboardForm && (
        <Card className="border-primary">
          <CardHeader className="bg-primary/5">
            <CardTitle>{editingEmployee ? 'Edit Employee' : 'Onboard New Employee'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleOnboardSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <Input
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@florentinainfra.com"
                    disabled={editingEmployee !== null}
                  />
                </div>
              </div>

              {!editingEmployee && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Password *</label>
                    <Input
                      required
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Initial login password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Role *</label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="employee">Employee</option>
                      <option value="hr">HR</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <Input
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 9876543210"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Department *</label>
                  <Input
                    required
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    placeholder="Engineering"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Designation *</label>
                  <Input
                    required
                    value={formData.designation}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    placeholder="Senior Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Monthly Salary (₹) *</label>
                  <Input
                    required
                    type="number"
                    value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: e.target.value})}
                    placeholder="65000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date of Joining *</label>
                  <Input
                    required
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PAN</label>
                  <Input
                    value={formData.pan}
                    onChange={(e) => setFormData({...formData, pan: e.target.value})}
                    placeholder="ABCDE1234F"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">UAN</label>
                  <Input
                    value={formData.uan}
                    onChange={(e) => setFormData({...formData, uan: e.target.value})}
                    placeholder="123456789012"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bank Account</label>
                  <Input
                    value={formData.bank_account}
                    onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                    placeholder="1234567890"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Full address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Emergency Contact</label>
                <Input
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                  placeholder="Contact person name and phone"
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  <UserPlus size={18} className="mr-2" />
                  {editingEmployee ? 'Update Employee' : 'Onboard Employee'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Search by name, ID, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Department</label>
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept === 'all' ? 'All Departments' : dept}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card>
        <CardHeader>
          <CardTitle>All Employees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredEmployees.map((employee) => (
              <div key={employee.employee_id} className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-bold text-lg">{employee.full_name}</h4>
                    <Badge variant="outline" className="text-xs">
                      {employee.employee_id}
                    </Badge>
                    <Badge className={employee.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Email:</span> {employee.email}
                    </div>
                    <div>
                      <span className="font-medium">Department:</span> {employee.department}
                    </div>
                    <div>
                      <span className="font-medium">Designation:</span> {employee.designation}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600 mt-1">
                    <div>
                      <span className="font-medium">Phone:</span> {employee.phone}
                    </div>
                    <div>
                      <span className="font-medium">Joining:</span> {new Date(employee.date_of_joining).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">Salary:</span> ₹{employee.salary.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEdit(employee)}
                  >
                    <Edit2 size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleStatus(employee.employee_id, employee.is_active)}
                    className={employee.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}
                  >
                    {employee.is_active ? <ToggleRight size={16} className="mr-1" /> : <ToggleLeft size={16} className="mr-1" />}
                    {employee.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {filteredEmployees.length === 0 && (
            <p className="text-center py-8 text-gray-500">No employees found</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HREmployees;
